import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Rocket, Barcode, Tag, Search, CheckCircle2, AlertTriangle, Save } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const base = process.env.REACT_APP_BACKEND_URL;

export default function GoLive() {
  const [sub, setSub] = useState("pricer");
  return (
    <div data-testid="golive-tools" className="max-w-6xl">
      <div className="flex items-center gap-2 mb-1"><Rocket className="h-6 w-6 text-sky-600" /><h1 className="font-display text-2xl font-bold text-slate-900">Go-Live Tools</h1></div>
      <p className="text-sm text-slate-500 mb-4">Get your imported catalogue shop-ready: price &amp; barcode the remaining drafts, then check every part will scan at the register.</p>
      <div className="flex gap-1 border-b-2 border-slate-200 mb-4">
        {[["pricer", "Bulk Pricer", Tag], ["barcode", "Barcode Check", Barcode]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`golive-tab-${k}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-[13px] font-bold rounded-t-md border border-b-0 ${sub === k ? "bg-white text-sky-700 border-slate-200" : "bg-slate-50 text-slate-500 border-transparent hover:bg-white"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {sub === "pricer" ? <BulkPricer /> : <BarcodeCheck />}
    </div>
  );
}

function BulkPricer() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({}); // id -> {price,cost_price,barcode,sku,category,active}
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const all = await api("GET", "/products");
    setRows(all.filter((p) => p.active === false).sort((a, b) => (a.brand || "").localeCompare(b.brand || "") || (a.name || "").localeCompare(b.name || "")));
    setEdits({});
  }, []);
  useEffect(() => { load().catch(() => toast.error("Could not load drafts")); }, [load]);

  const val = (p, f) => (edits[p.id]?.[f] !== undefined ? edits[p.id][f] : p[f] ?? "");
  const setVal = (id, f, v) => setEdits((e) => ({ ...e, [id]: { ...e[id], [f]: v } }));

  const shown = useMemo(() => {
    const s = q.toLowerCase();
    return rows.filter((p) => !s || `${p.name} ${p.brand} ${p.category} ${p.sku}`.toLowerCase().includes(s));
  }, [rows, q]);

  const dirtyIds = Object.keys(edits).filter((id) => Object.keys(edits[id] || {}).length);

  const buildPayload = (ids, activate) => ids.map((id) => {
    const e = edits[id] || {};
    const row = { id };
    if (e.price !== undefined) row.price = parseFloat(e.price) || 0;
    if (e.cost_price !== undefined) row.cost_price = parseFloat(e.cost_price) || 0;
    if (e.barcode !== undefined) row.barcode = String(e.barcode).trim();
    if (e.sku !== undefined) row.sku = String(e.sku).trim();
    if (activate) row.active = true;
    return row;
  });

  const priceOf = (p) => { const e = edits[p.id] || {}; return e.price !== undefined ? (parseFloat(e.price) || 0) : (p.price || 0); };
  const saveAll = async (activate) => {
    const targetIds = activate ? shown.filter((p) => priceOf(p) > 0).map((p) => p.id) : dirtyIds;
    if (!targetIds.length) { toast.error(activate ? "Set a price on at least one draft in view before activating" : "No changes to save"); return; }
    setSaving(true);
    try {
      const res = await api("POST", "/products/bulk-update", buildPayload(targetIds, activate));
      toast.success(activate ? `Priced & activated ${res.updated} item(s) — now live` : `Saved ${res.updated} item(s)`);
      await load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const activateOne = async (p) => {
    const price = parseFloat(val(p, "price")) || 0;
    if (price <= 0) { toast.error("Set a price before activating"); return; }
    setSaving(true);
    try {
      await api("POST", "/products/bulk-update", buildPayload([p.id], true));
      toast.success(`${p.name} is live`);
      await load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div data-testid="bulk-pricer">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="pricer-search" placeholder="Filter drafts by name / brand / category…" className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm" />
        </div>
        <span data-testid="pricer-count" className="text-[12px] font-bold text-slate-500">{shown.length} draft(s)</span>
        <button onClick={() => saveAll(false)} disabled={saving || !dirtyIds.length} data-testid="pricer-save" className="rounded-lg bg-slate-700 text-white font-bold px-3 py-2 text-sm flex items-center gap-1.5 disabled:opacity-50"><Save className="h-4 w-4" /> Save changes</button>
        <button onClick={() => saveAll(true)} disabled={saving} data-testid="pricer-activate-all" className="rounded-lg bg-emerald-600 text-white font-bold px-3 py-2 text-sm flex items-center gap-1.5 disabled:opacity-50"><CheckCircle2 className="h-4 w-4" /> Save &amp; activate priced rows in view</button>
      </div>
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[minmax(180px,2fr)_90px_90px_90px_140px_120px_80px] gap-2 bg-slate-50 px-3 py-2 text-[11px] font-black uppercase tracking-wide text-slate-500 sticky top-0">
          <span>Product</span><span>Brand</span><span>Cost $</span><span>Price $</span><span>Barcode</span><span>SKU</span><span></span>
        </div>
        <div className="max-h-[62vh] overflow-auto divide-y divide-slate-100">
          {shown.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No drafts left — everything's priced &amp; live. 🎉</p>}
          {shown.map((p) => (
            <div key={p.id} className="grid grid-cols-[minmax(180px,2fr)_90px_90px_90px_140px_120px_80px] gap-2 items-center px-3 py-1.5" data-testid={`pricer-row-${p.id}`}>
              <div className="min-w-0 flex items-center gap-2">
                <div className="h-8 w-8 shrink-0 bg-slate-50 border rounded overflow-hidden flex items-center justify-center">{p.image_url ? <img src={p.image_url.startsWith("http") ? p.image_url : `${base}${p.image_url}`} alt="" className="w-full h-full object-contain" /> : <span className="text-[8px] text-slate-300">—</span>}</div>
                <div className="min-w-0"><p className="text-[13px] font-semibold truncate">{p.name}</p><p className="text-[10px] text-slate-400 truncate">{p.category || "—"}</p></div>
              </div>
              <span className="text-[12px] text-slate-500 truncate">{p.brand || "—"}</span>
              <input type="number" value={val(p, "cost_price")} onChange={(e) => setVal(p.id, "cost_price", e.target.value)} data-testid={`pricer-cost-${p.id}`} className="rounded border border-slate-300 px-2 py-1 text-[13px] w-full" />
              <input type="number" value={val(p, "price")} onChange={(e) => setVal(p.id, "price", e.target.value)} data-testid={`pricer-price-${p.id}`} className="rounded border border-slate-300 px-2 py-1 text-[13px] w-full" />
              <input value={val(p, "barcode")} onChange={(e) => setVal(p.id, "barcode", e.target.value)} data-testid={`pricer-barcode-${p.id}`} placeholder="scan…" className="rounded border border-slate-300 px-2 py-1 text-[13px] w-full" />
              <input value={val(p, "sku")} onChange={(e) => setVal(p.id, "sku", e.target.value)} data-testid={`pricer-sku-${p.id}`} className="rounded border border-slate-300 px-2 py-1 text-[13px] w-full" />
              <button onClick={() => activateOne(p)} disabled={saving} data-testid={`pricer-activate-${p.id}`} className="rounded bg-emerald-600 text-white text-[11px] font-black px-2 py-1.5 hover:bg-emerald-700 disabled:opacity-50">Go live</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BarcodeCheck() {
  const [audit, setAudit] = useState(null);
  const [scan, setScan] = useState("");
  const [result, setResult] = useState(null);

  const load = useCallback(async () => { try { setAudit(await api("GET", "/products/barcode-audit")); } catch { toast.error("Audit failed"); } }, []);
  useEffect(() => { load(); }, [load]);

  const doScan = async (code) => {
    const c = String(code || "").trim();
    if (!c) return;
    try { const p = await api("GET", `/products/barcode/${encodeURIComponent(c)}`); setResult({ ok: true, p }); }
    catch { setResult({ ok: false, code: c }); }
  };

  return (
    <div data-testid="barcode-check" className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-[12px] font-bold text-slate-500 mb-1">Test a scan</p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Barcode className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input autoFocus value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { doScan(scan); setScan(""); } }} data-testid="barcode-scan-input" placeholder="Scan or type a barcode, press Enter…" className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm" />
          </div>
          <button onClick={() => { doScan(scan); setScan(""); }} data-testid="barcode-scan-btn" className="rounded-lg bg-sky-600 text-white font-bold px-4 py-2 text-sm">Check</button>
        </div>
        {result && (result.ok
          ? <div data-testid="scan-result-ok" className="mt-3 flex items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3"><CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /><div className="text-sm"><b>{result.p.name}</b> <span className="text-slate-500">— {result.p.brand || "—"} · {money(result.p.price)} · stock {result.p.qty ?? 0}</span></div></div>
          : <div data-testid="scan-result-miss" className="mt-3 flex items-center gap-3 rounded-lg bg-rose-50 border border-rose-200 p-3"><AlertTriangle className="h-5 w-5 text-rose-600 shrink-0" /><div className="text-sm text-rose-700">No product has barcode <b>{result.code}</b> — it won't scan at the register.</div></div>)}
      </div>

      {audit && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Sellable parts", audit.sellable_total, "text-slate-900"], ["Ready to scan", audit.with_barcode, "text-emerald-600"], ["Missing barcode", audit.missing_count, "text-amber-600"], ["Duplicate barcodes", audit.duplicate_count, "text-rose-600"]].map(([label, v, c]) => (
              <div key={label} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`audit-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
                <p className={`text-2xl font-black ${c}`}>{v}</p>
              </div>
            ))}
          </div>
          {audit.duplicate_count > 0 && (
            <div className="bg-white border border-rose-200 rounded-xl p-4">
              <p className="font-bold text-rose-700 text-sm mb-2">Duplicate barcodes (will scan the wrong item)</p>
              <div className="divide-y max-h-40 overflow-auto">
                {audit.duplicates.map((d) => <div key={d.barcode} className="py-1.5 text-[13px]"><b className="font-mono">{d.barcode}</b> → {d.products.map((x) => `${x.name} (${x.brand || "—"})`).join("  ·  ")}</div>)}
              </div>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="font-bold text-slate-700 text-sm mb-2">Parts with no barcode <span className="text-slate-400 font-normal">(add them in the Bulk Pricer)</span></p>
            {audit.missing_count === 0
              ? <p className="text-sm text-emerald-600">Every sellable part has a barcode. Ready for go-live. ✅</p>
              : <div className="divide-y max-h-56 overflow-auto" data-testid="audit-missing-list">{audit.missing_sample.map((p) => <div key={p.id} className="py-1.5 text-[13px] flex justify-between"><span className="truncate">{p.name} <span className="text-slate-400">— {p.brand || "—"}</span></span><span className="text-slate-400 text-[11px]">{p.sku || "no sku"}</span></div>)}{audit.missing_count > audit.missing_sample.length && <p className="text-[12px] text-slate-400 py-2">…and {audit.missing_count - audit.missing_sample.length} more</p>}</div>}
          </div>
        </>
      )}
    </div>
  );
}
